import type { Cluster } from './types'

/**
 * Whether an incident is still in progress: the alerts dot, the dashboard's
 * tile dot and, through isClipReady, the clip panel all hold to this one line.
 *
 * Two lines disagreed before. The dot answered "can a new detection still
 * join" and the clip "has every detection ended", so an incident that ended
 * ten seconds ago put a playable clip beside a green dot, and a parked car
 * Frigate kept tracking put "Recording…" beside a grey one.
 *
 * The moment is the server's finishes_at, already resolved: it includes the
 * 15 minutes a silent detection may hold an incident, which nothing announces
 * when it passes. A finish still in the future counts as in progress — clock
 * skew between a site and here is ordinary, and the most recent thing there
 * is should not read as the oldest.
 */
export function isInProgress(cluster: Cluster, nowMs: number = Date.now()): boolean {
  // Strictly less than, so the instant finishesAtMs names is already finished.
  // With <=, a consumer that wakes itself then would find it still in progress
  // and have nothing left scheduled to re-check it.
  return nowMs < finishesAtMs(cluster)
}

/**
 * Whether new detections can still join this incident — fresh activity, as
 * against an incident merely still in progress. The dashboard pulses on it, so
 * a camera watching a parked car is steady green for the hours Frigate tracks
 * it rather than pulsing throughout.
 */
export function isStillGrowing(cluster: Cluster, nowMs: number = Date.now()): boolean {
  return nowMs < cluster.settled_at * 1000
}

/**
 * When this incident stops being in progress, as an epoch in milliseconds.
 * What a consumer arms a timer on.
 *
 * A server that predates finishes_at gives no moment for an incident with a
 * detection still running — it is in progress until the data says otherwise,
 * as the clip panel always treated it — and settled_at once all have ended.
 */
export function finishesAtMs(cluster: Cluster): number {
  if (cluster.finishes_at !== undefined) return cluster.finishes_at * 1000
  return cluster.all_ended ? cluster.settled_at * 1000 : Infinity
}

/**
 * When this incident's clip could first become available, as an epoch in
 * milliseconds. What a consumer arms a timer on.
 *
 * A tamper incident answers on its evidence window, which can be long past
 * while the incident itself is still running — so arming on finishes_at, as
 * everything else does, would wait for a covered lens to be uncovered.
 */
export function clipReadyAtMs(cluster: Cluster): number {
  if (cluster.label === 'tamper') {
    return ((cluster.primary_time ?? cluster.start_time) + tamperEvidenceSeconds) * 1000
  }
  return finishesAtMs(cluster)
}

/**
 * Whether this incident's clip is finished and worth offering.
 *
 * Shared by the clip panel and the acknowledge gate, so a dispatcher cannot
 * resolve an incident whose footage they have not been able to watch.
 *
 * A tamper incident is the exception, and answers on its own evidence window
 * rather than on the incident ending — see tamperEvidenceReady.
 *
 * Both halves matter for everything else, and both were observed failing on
 * the running system:
 * an incident still running has nothing written to serve, and one that ended
 * inside the merge window can be rejoined by the next detection — `all_ended`
 * was seen going true then false again five times in a minute.
 */
export function isClipReady(cluster: Cluster, nowMs: number = Date.now()): boolean {
  if (cluster.label === 'tamper') return tamperEvidenceReady(cluster, nowMs)
  return !isInProgress(cluster, nowMs)
}

/**
 * How long after the tampering its evidence is complete.
 *
 * A covered lens produces no motion, so the recorder stops within seconds of
 * the act — measured at 2s and 14s on two real covers. The window only has to
 * outlast that.
 */
const tamperEvidenceSeconds = 15

/**
 * Whether a tamper incident's evidence is complete.
 *
 * The evidence and the incident are different windows, and treating them as
 * one is what made this footage useless. A covered lens is an incident that
 * can last hours; the evidence is the approach and the act, and once the lens
 * is covered the footage is a blank frame carrying nothing. Waiting for the
 * incident to end delivered the recording of someone covering a camera only
 * once somebody uncovered it.
 *
 * Measured on incident 5787: the tampering was at 23:14:24 and the recording
 * holding it was finished at 23:14:43, while the clip stayed unavailable for
 * minutes.
 */
function tamperEvidenceReady(cluster: Cluster, nowMs: number): boolean {
  const moment = cluster.primary_time ?? cluster.start_time
  return nowMs / 1000 >= moment + tamperEvidenceSeconds
}
